import ApiService from '../services/api';
import { hasCompletedWorkflowPlanner } from './workflowCompletion';
import { isPartnerPortalSession } from './partnerPortalSession';
import { isAdminPortalUser, getCurrentUserRole, isSupportPortalUser, isExpertPortalUser } from './authRole';
import { resolveMsmeOnboardingDestination } from './msmeOnboardingRouting';
import { hasDeclaredExportActivity } from './msmeExportIntent';
import { firstIncompleteMyCompanyPage } from './myCompanyRequired';

/** Role-aware destination after a successful unified login. */
export const POST_LOGIN_ROUTES: Record<string, string> = {
  admin: '/admin/dashboard',
  view: '/admin/dashboard',
  support: '/support/dashboard',
  expert: '/expert/dashboard',
  partner: '/partner/dashboard',
};

export async function resolveEnterprisePostLoginDestination(): Promise<string> {
  try {
    const profileResponse = await ApiService.getEnterpriseProfile();
    if (!profileResponse?.data?.companyName) {
      return '/enterprise/registration';
    }
    return '/enterprise/dashboard';
  } catch (profileError: unknown) {
    const message = profileError instanceof Error ? profileError.message : '';
    if (message.toLowerCase().includes('not found')) {
      return '/enterprise/registration';
    }
    return '/enterprise/registration';
  }
}

export async function resolveMsmePostLoginDestination(): Promise<string> {
  try {
    const [profileResponse, statsResponse] = await Promise.all([
      ApiService.getMSMEProfile(),
      ApiService.getMSMEStats().catch(() => null),
    ]);
    const profileData = profileResponse?.data;
    const statsData =
      (statsResponse as { data?: Record<string, unknown> } | null)?.data
      ?? (statsResponse as Record<string, unknown> | null);
    const carbon = statsData?.carbon as
      | { totalCO2Emissions?: number; currentScore?: number }
      | undefined;
    const bulkUpload = statsData?.documentBulkUpload as
      | { totalDocuments?: number; totalTransactions?: number }
      | undefined;

    if (!profileData?.companyName) {
      return '/msme-registration';
    }
    const nextCompanyPage = firstIncompleteMyCompanyPage(profileData);
    if (nextCompanyPage) {
      return nextCompanyPage.path;
    }

    return resolveMsmeOnboardingDestination({
      hasCompanyName: Boolean(profileData?.companyName),
      hasProfile: Boolean(
        profileData?.companyName
        && profileData?.gstNumber
        && hasDeclaredExportActivity(profileData)
      ),
      hasDocuments:
        Number(bulkUpload?.totalDocuments ?? 0) > 0
        || Number(bulkUpload?.totalTransactions ?? 0) > 0,
      hasAssessment:
        Number(carbon?.totalCO2Emissions || 0) > 0 || Number(carbon?.currentScore || 0) > 0,
      workflowCompleted: hasCompletedWorkflowPlanner(
        profileData?.business?.manufacturingWorkflow
      ),
    });
  } catch (profileError: unknown) {
    const message = profileError instanceof Error ? profileError.message : '';
    if (message.toLowerCase().includes('not found')) {
      return '/msme-registration';
    }
  }

  return '/dashboard';
}

/** Where to send an already-authenticated user who opens /login. */
export function resolveAuthenticatedHomePath(): string {
  if (isAdminPortalUser()) {
    return '/admin/dashboard';
  }

  if (isSupportPortalUser()) {
    return '/support/dashboard';
  }

  if (isExpertPortalUser()) {
    return '/expert/dashboard';
  }

  if (isPartnerPortalSession() || getCurrentUserRole() === 'partner') {
    return '/partner/dashboard';
  }

  if (getCurrentUserRole() === 'enterprise') {
    return '/enterprise/dashboard';
  }

  return '/dashboard';
}
